'use client';

import {
  ModalDialog,
  Modal,
  ModalOverlay,
  Button,
  CloseButton,
  Form,
  LoadingSpinner,
  Alert,
  Input,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  HorizontalEventCard,
  Checkbox,
  Label,
  Select,
  DatePicker
} from '@goodneighbor/ui';
import { cleanPhoneNumber, cleanCountryCode } from '@/services/phone';
import styles from './externalRegistrationModal.module.scss';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Event } from '@goodneighbor/api/schemas/events/event.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import useRegisterEventMutation from '@/hooks/api/events/useRegisterEventMutation';
import { useEffect, useState } from 'react';
import { cx } from 'class-variance-authority';
import useAccountQuery from '@/hooks/useAccountQuery';
import LogInForm from '@/app/volunteer/form';
import {
  ACTION_TYPES,
  CLIENT_ERROR_MESSAGE as CAPTCHA_ERROR_MESSAGE
} from '@goodneighbor/api/services/recaptcha/constants';
import { parseForEventCardProps } from '@/services/event';
import { toCalendarDateValue, toDateString } from '@/services/dateTime';
import { useHasJWT } from '@/hooks/useJWT';
import { removeJWT } from '@/services/jwt';
import { Flag01, LinkExternal02 } from '@goodneighbor/ui/icons/untitled';

const externalRegistrationSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  phone: z.string().min(1, { message: 'Phone number is required' }),
  email: z.string().email({ message: 'Valid email is required' }),
  birthDate: z.string().min(1, { message: 'Date of birth is required' }),
  gender: z.string().min(1, { message: 'Gender is required' }),
  acceptedPhoneTermsAt: z.string().min(1, {
    message: 'You must agree to receive text messages from the organization'
  })
});

type ExternalRegistrationFormData = z.infer<typeof externalRegistrationSchema>;

type ExternalRegistrationModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  event: Event;
  organizationName: string;
  defaultValues?: Partial<ExternalRegistrationFormData>;
  accountData?: ReturnType<typeof useAccountQuery>['data'];
  isSignedIn?: boolean;
};

function ExternalRegistrationModal({
  isOpen,
  onOpenChange,
  event,
  organizationName,
  defaultValues,
  accountData,
  isSignedIn
}: ExternalRegistrationModalProps) {
  const [step, setStep] = useState<'form' | 'logIn' | 'success'>('form');
  const [error, setError] = useState<string | null>(null);

  const registerEventMutation = useRegisterEventMutation({
    eventId: event.id,
    onSuccess: () => {
      setError(null);
      setStep('success');
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const form = useForm<ExternalRegistrationFormData>({
    resolver: zodResolver(externalRegistrationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      birthDate: '',
      gender: '',
      acceptedPhoneTermsAt: '',
      ...defaultValues
    }
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setStep('form');
      setError(null);
    }
    onOpenChange(isOpen);
  };

  const handleContinueToPartner = () => {
    if (event.sourceUrl) {
      window.open(event.sourceUrl, '_blank', 'noopener,noreferrer');
      onOpenChange(false);
    }
  };

  const onSubmit = async (data: ExternalRegistrationFormData) => {
    const now = new Date().toISOString();
    window.grecaptcha.ready(async function () {
      try {
        const token = await grecaptcha.execute(
          process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY || '',
          { action: ACTION_TYPES.EVENT_REGISTRATION }
        );
        registerEventMutation.mutate({
          ...data,
          eventId: event.id,
          phone: cleanPhoneNumber(accountData?.data.phone || data.phone),
          birthDate: new Date(data.birthDate).toISOString(),
          gender: data.gender,
          isInternalVolunteer: false,
          acceptedCancellationPolicyAt: now,
          acceptedTermsAndConditionsAt: now,
          prefersSpanishCommunication: false,
          recaptchaToken: token
        });
      } catch {
        setError(CAPTCHA_ERROR_MESSAGE);
      }
    });
  };

  if (step === 'logIn') {
    return (
      <ModalOverlay
        className={styles.overlay}
        isDismissable
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        <Modal>
          <ModalDialog>
            <div className={cx(styles.registrationModal)}>
              <div className={styles.header}>
                <h1 className={styles.title}>Sign In</h1>
                <CloseButton
                  size="lg"
                  onClick={() => handleOpenChange(false)}
                />
              </div>
              <div className={cx(styles.content, styles.logInForm)}>
                <LogInForm />
              </div>
            </div>
          </ModalDialog>
        </Modal>
      </ModalOverlay>
    );
  }

  const eventCardProps = parseForEventCardProps(event, organizationName);

  return (
    <ModalOverlay
      className={styles.overlay}
      isDismissable={step !== 'success'}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <Modal>
        {() => (
          <ModalDialog>
            {() => (
              <Form {...form}>
                <form
                  className={cx(
                    styles.registrationModal,
                    step === 'success' && styles.successModal
                  )}
                  onSubmit={form.handleSubmit(onSubmit)}
                >
                  {step === 'success' ? (
                    <>
                      <div className={styles.header}>
                        <h1 className={styles.title}>
                          Continue to partner registration
                        </h1>
                        <CloseButton
                          size="lg"
                          onClick={() => handleOpenChange(false)}
                        />
                      </div>
                      <div className={styles.successContent}>
                        <img
                          src="/placeholder/celebration.svg"
                          alt=""
                          className={styles.successImage}
                          aria-hidden
                        />
                        <h2 className={styles.successTitle}>
                          You&apos;re almost done!
                        </h2>
                        <p className={styles.successDescription}>
                          You&apos;ve been entered to win exclusive prizes.
                          {event.sourceUrl
                            ? ' You will be redirected to complete your Event registration on the external platform.'
                            : ' Contact the organizer directly to complete your registration.'}
                        </p>
                      </div>
                      <div className={styles.footer}>
                        <Button
                          onClick={handleContinueToPartner}
                          className={styles.submitButton}
                          size="lg"
                          isDisabled={!event.sourceUrl}
                          iconTrailing={() => (
                            <LinkExternal02 width={20} height={20} />
                          )}
                        >
                          Continue to partner registration
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.header}>
                        <h1 className={styles.title}>Event Registration</h1>
                        <CloseButton
                          size="lg"
                          onClick={() => handleOpenChange(false)}
                        />
                      </div>
                      <div className={styles.content}>
                        <HorizontalEventCard {...eventCardProps} />
                        <div className={styles.volunteerHeader}>
                          <h2 className={styles.volunteerTitle}>
                            Volunteer Information
                          </h2>
                          {!isSignedIn && (
                            <div>
                              <span className={styles.signIn}>
                                Have an account?{' '}
                              </span>
                              <Button
                                onClick={() => setStep('logIn')}
                                color="link-color"
                                size="xs"
                              >
                                Sign in
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className={styles.form}>
                          <div className={styles.row}>
                            <FormField
                              control={form.control}
                              name="firstName"
                              render={({ field, fieldState: { error } }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      isInvalid={!!error}
                                      isDisabled={!!defaultValues?.firstName}
                                      isRequired
                                      value={field.value ?? ''}
                                      onChange={(value) =>
                                        field.onChange(value)
                                      }
                                      id="firstName"
                                      label="First name"
                                      placeholder="First Name"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="lastName"
                              render={({ field, fieldState: { error } }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      isInvalid={!!error}
                                      isDisabled={!!defaultValues?.lastName}
                                      isRequired
                                      value={field.value ?? ''}
                                      onChange={(value) =>
                                        field.onChange(value)
                                      }
                                      id="lastName"
                                      label="Last name"
                                      placeholder="Last Name"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className={styles.row}>
                            <FormField
                              control={form.control}
                              name="phone"
                              render={({ field, fieldState: { error } }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      isInvalid={!!error}
                                      isDisabled={!!defaultValues?.phone}
                                      isRequired
                                      value={field.value ?? ''}
                                      onChange={(value) =>
                                        field.onChange(value)
                                      }
                                      icon={Flag01}
                                      id="phone"
                                      label="Phone number"
                                      format="+1 (###) ###-####"
                                      placeholder="+1 (555) 123-4567"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="email"
                              render={({ field, fieldState: { error } }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      isInvalid={!!error}
                                      isDisabled={!!defaultValues?.email}
                                      isRequired
                                      value={field.value ?? ''}
                                      onChange={(value) =>
                                        field.onChange(value)
                                      }
                                      id="email"
                                      label="Email"
                                      hideIcon
                                      placeholder="name@example.com"
                                      type="email"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className={styles.row}>
                            <FormField
                              control={form.control}
                              name="birthDate"
                              render={({ field }) => (
                                <FormItem className={styles.formItem}>
                                  <FormControl>
                                    <div>
                                      <Label
                                        className={styles.inputLabel}
                                        isRequired
                                        id="birthDate"
                                      >
                                        Date of birth
                                      </Label>
                                      <DatePicker
                                        {...field}
                                        className={cx(
                                          styles.datePicker,
                                          !field.value && styles.placeholder
                                        )}
                                        aria-labelledby="birthDate"
                                        presetButtonLabel="Select date"
                                        onChange={(value) => {
                                          field.onChange(toDateString(value));
                                        }}
                                        showActions={false}
                                        shouldCloseOnSelect
                                        value={toCalendarDateValue(field.value)}
                                        isDisabled={!!defaultValues?.birthDate}
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="gender"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Select
                                      isDisabled={!!defaultValues?.gender}
                                      value={field.value ?? ''}
                                      onChange={(value) => {
                                        field.onChange(value as string);
                                        form.clearErrors('gender');
                                      }}
                                      id="gender"
                                      label="Gender"
                                      isRequired
                                      placeholder="Select gender"
                                      items={[
                                        { label: 'Male', id: 'male' },
                                        { label: 'Female', id: 'female' }
                                      ]}
                                    >
                                      {(item) => (
                                        <Select.Item key={item.id} id={item.id}>
                                          {item.label}
                                        </Select.Item>
                                      )}
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <FormField
                            control={form.control}
                            name="acceptedPhoneTermsAt"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Checkbox
                                    isSelected={!!field.value}
                                    onChange={(value) => {
                                      field.onChange(
                                        value ? new Date().toISOString() : ''
                                      );
                                      form.clearErrors('acceptedPhoneTermsAt');
                                    }}
                                    id="acceptedPhoneTermsAt"
                                    label="By providing my phone number, I agree to receive text messages from the organization."
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <img
                          src="/placeholder/prize-alert.png"
                          alt="Sign up to win exclusive prizes!"
                          className={styles.giveawayBanner}
                        />
                        {error && (
                          <Alert
                            color="error"
                            title="There was a problem"
                            showCloseButton
                            description={error}
                            onClose={() => setError(null)}
                          />
                        )}
                      </div>
                      <div className={styles.footer}>
                        <Button
                          isLoading={registerEventMutation.isPending}
                          type="submit"
                          className={styles.submitButton}
                          size="lg"
                        >
                          Continue
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </Form>
            )}
          </ModalDialog>
        )}
      </Modal>
    </ModalOverlay>
  );
}

export default function ExternalRegistrationModalWrapper(
  props: ExternalRegistrationModalProps
) {
  const hasJWT = useHasJWT();
  const { data, isLoading, isError } = useAccountQuery({
    enabled: hasJWT
  });

  useEffect(() => {
    if (isError && hasJWT) {
      removeJWT();
    }
  }, [hasJWT, isError]);

  if (props.isOpen && hasJWT && isLoading && !isError && !data) {
    return (
      <ModalOverlay isOpen={props.isOpen} className={styles.overlay}>
        <Modal>
          <ModalDialog>
            <LoadingSpinner />
          </ModalDialog>
        </Modal>
      </ModalOverlay>
    );
  }

  const defaultValues = {
    firstName: data?.data.firstName || '',
    lastName: data?.data.lastName || '',
    phone: cleanCountryCode(data?.data.phone || ''),
    email: data?.data.email || '',
    birthDate: data?.data.birthDate || '',
    gender: data?.data.gender || '',
    acceptedPhoneTermsAt: ''
  };

  return (
    <ExternalRegistrationModal
      {...props}
      isSignedIn={!!data}
      accountData={data}
      defaultValues={defaultValues}
    />
  );
}
